import type { SlideFields } from "@/types/Slide";
import type { ReadingSlideData } from "@ai-teach/types";
import { AiFillPicture } from "react-icons/ai";
import { FaVideo } from "react-icons/fa";

interface ReadingFluencyThumbnailProps {
  slide: SlideFields;
}

export function ReadingFluencyThumbnail({ slide }: ReadingFluencyThumbnailProps) {
  const readingSlideData = slide.slideData as ReadingSlideData;
  const passages = readingSlideData.passages || [];
  const isTextOnly = readingSlideData.isTextOnly;

  return (
    <div className="bg-accent-100 h-full flex flex-col px-2 py-1.5 gap-1.5 overflow-hidden">
      {passages.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[9px] font-medium text-blue-300">No passages yet</span>
        </div>
      ) : (
        passages.slice(0, 3).map((passage, index) => (
          <div key={passage.id || index} className="flex gap-1 shrink-0">
            {/* Text card — 60% width (or full width if text-only) */}
            <div
              className={`${isTextOnly ? "w-full" : "w-[60%]"} bg-white rounded-lg overflow-hidden flex shadow-sm`}
            >
              <div className="bg-accent-800 flex items-center justify-center px-1.5 shrink-0">
                <span
                  className="text-white font-black leading-none"
                  style={{ fontSize: "6px", letterSpacing: "0.05em" }}
                >
                  {index + 1}
                </span>
              </div>
              <p className="text-[7px] text-gray-600 leading-tight px-2 py-1 line-clamp-5">
                {passage.text || "No text"}
              </p>
            </div>

            {/* Visual — 40% width */}
            {!isTextOnly && (
              <div
                className={`w-[40%] shrink-0 rounded-lg overflow-hidden border-2 border-accent-800 bg-white ${
                  passage.mediaType === "video" ? "aspect-video" : "aspect-square"
                }`}
              >
                {passage.mediaType === "video" ? (
                  passage.video?.src ? (
                    <div className="relative w-full h-full bg-black">
                      <video
                        src={passage.video.src}
                        preload="metadata"
                        muted
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <FaVideo className="text-white/70 text-sm" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-accent-50">
                      <FaVideo className="text-accent-200 text-lg" />
                    </div>
                  )
                ) : passage.image?.src ? (
                  <img
                    src={passage.image.src}
                    alt={passage.text || "passage image"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-accent-50">
                    <AiFillPicture className="text-accent-200 text-lg" />
                  </div>
                )}
              </div>
            )}
          </div>
        ))
      )}

      {passages.length > 3 && (
        <div className="flex items-center justify-center py-0.5 px-2 bg-white rounded-lg shadow-sm">
          <span className="text-[9px] font-medium text-accent-400">
            +{passages.length - 3} more
          </span>
        </div>
      )}
    </div>
  );
}
